import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Inbox } from "lucide-react";

export default function Qualification() {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/qualification", { params: { status: statusFilter } });
      setItems(data);
    } catch {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const update = async (id, status) => {
    try {
      await api.patch(`/qualification/${id}`, { status });
      toast.success(status);
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <Layout title="Qualification queue" subtitle="Reply triage" actions={
      <div className="flex items-center gap-1 bg-card border border-border p-1" style={{ borderRadius: 2 }}>
        {["pending", "approved", "dismissed", "all"].map((s) => (
          <button key={s} onClick={() => setStatusFilter(s)} className={`px-3 py-1.5 text-xs capitalize ${statusFilter === s ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`} style={{ borderRadius: 2 }} data-testid={`qual-filter-${s}`}>{s}</button>
        ))}
      </div>
    }>
      {loading && <div className="text-sm text-muted-foreground">Loading…</div>}
      {!loading && items.length === 0 && (
        <div className="bg-card border border-border p-12 text-center" style={{ borderRadius: 2 }}>
          <Inbox className="h-6 w-6 text-muted-foreground mx-auto mb-3" strokeWidth={1.5} />
          <div className="text-sm text-muted-foreground">No items here. Replies are routed here when you classify them from a lead.</div>
        </div>
      )}
      <div className="space-y-3">
        {items.map((it) => (
          <div key={it.item_id} className="bg-card border border-border p-5" style={{ borderRadius: 2 }} data-testid={`qual-${it.item_id}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="overline">{it.classification?.intent} · {Math.round((it.classification?.confidence || 0) * 100)}%</div>
              <div className="text-xs text-muted-foreground">{new Date(it.created_at).toLocaleString("en-AU")}</div>
            </div>
            <div className="text-sm whitespace-pre-wrap mb-3 border-l-2 border-border pl-3 text-foreground/80">{it.inbound_text}</div>
            {it.classification?.suggested_reply && (
              <div className="bg-secondary/50 p-3 mt-3 font-mono text-xs whitespace-pre-wrap" style={{ borderRadius: 2 }}>
                <div className="overline mb-1">Suggested reply</div>
                {it.classification.suggested_reply}
              </div>
            )}
            <div className="text-xs text-muted-foreground mt-2">Action: {it.classification?.suggested_action}</div>
            {it.status === "pending" && (
              <div className="flex gap-2 mt-4">
                <Button size="sm" onClick={() => update(it.item_id, "approved")} data-testid={`qual-approve-${it.item_id}`} style={{ borderRadius: 2 }}>Approve</Button>
                <Button size="sm" variant="outline" onClick={() => update(it.item_id, "dismissed")} data-testid={`qual-dismiss-${it.item_id}`} style={{ borderRadius: 2 }}>Dismiss</Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </Layout>
  );
}
